// People: InitialsAvatar (Theme.swift: initials of the first two words on the user's colorHex gradient, white ring)
// with the presence dot from Team+Store.swift (Online < 15 min, Away < 8 h, else Offline), and AvatarStack
// (Views/Components/AvatarStack.swift: up to 4 overlapping avatars, "+N" in gray, "Unassigned" when empty).

import { UserRoundX } from 'lucide-react';
import type { CSSProperties } from 'react';
import { cn } from './cn';
import { GT_COLORS, initialsOf, mixHex, normalizeHex, readableTextOn } from './color';

export type Presence = 'online' | 'away' | 'offline';

/** Presence thresholds, as iOS `User.presenceTitle`. */
export const PRESENCE_ONLINE_MS = 15 * 60 * 1000;
export const PRESENCE_AWAY_MS = 8 * 60 * 60 * 1000;

export const PRESENCE_TITLES: Readonly<Record<Presence, string>> = {
  online: 'Online',
  away: 'Away',
  offline: 'Offline',
};

export const PRESENCE_COLORS: Readonly<Record<Presence, string>> = {
  online: GT_COLORS.green,
  away: GT_COLORS.amber,
  offline: GT_COLORS.grey,
};

/** Presence from the rep's last known position time (null/undefined → offline). Future times count as now. */
export function presenceOf(lastSeenAt: Date | string | number | null | undefined, now: number = Date.now()): Presence {
  if (lastSeenAt === null || lastSeenAt === undefined) return 'offline';
  const at = lastSeenAt instanceof Date ? lastSeenAt.getTime() : new Date(lastSeenAt).getTime();
  if (!Number.isFinite(at)) return 'offline';
  const age = Math.max(0, now - at);
  if (age < PRESENCE_ONLINE_MS) return 'online';
  if (age < PRESENCE_AWAY_MS) return 'away';
  return 'offline';
}

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const AVATAR_PX: Record<AvatarSize, number> = { xs: 20, sm: 24, md: 32, lg: 40, xl: 52 };

export interface AvatarProps {
  /** Full name; initials come from its first two words ("?" when empty). */
  name: string | null | undefined;
  /**
   * The user's colorHex. Missing (no profile) → navy, as InitialsAvatar's default; present but unparseable → gray,
   * as Theme.color(hex:) renders it on the phone.
   */
  colorHex?: string | null;
  size?: AvatarSize | number;
  presence?: Presence | null;
  /**
   * Accessible text. Defaults to the name (plus presence). Pass null when the name is printed right next to the
   * avatar, so screen readers do not read it twice.
   */
  label?: string | null;
  /** Ring color around the avatar (stacked avatars use the card surface so they separate). */
  ringClassName?: string;
  className?: string;
  style?: CSSProperties;
}

export function avatarPixels(size: AvatarSize | number): number {
  return typeof size === 'number' ? size : AVATAR_PX[size];
}

export function Avatar({ name, colorHex, size = 'md', presence, label, ringClassName, className, style }: AvatarProps) {
  const px = avatarPixels(size);
  const base = colorHex == null ? GT_COLORS.navy : normalizeHex(colorHex);
  const initials = initialsOf(name);
  const presenceText = presence ? PRESENCE_TITLES[presence] : null;
  const accessible =
    label === null ? null : (label ?? [name?.trim() || 'Unknown', presenceText].filter(Boolean).join(', '));
  const dot = Math.max(7, Math.round(px * 0.3));
  return (
    <span
      role={accessible ? 'img' : undefined}
      aria-label={accessible ?? undefined}
      aria-hidden={accessible ? undefined : true}
      title={accessible ?? undefined}
      className={cn('relative inline-flex shrink-0', className)}
      style={{ width: px, height: px, ...style }}
    >
      <span
        className={cn(
          'flex size-full items-center justify-center rounded-full font-rounded font-bold select-none',
          'shadow-[inset_0_0_0_0.75px_rgb(255_255_255/0.35)]',
          ringClassName,
        )}
        style={{
          backgroundImage: `linear-gradient(180deg, ${mixHex(base, '#FFFFFF', 0.14)}, ${base})`,
          backgroundColor: base,
          color: readableTextOn(base),
          fontSize: Math.max(8, Math.round(px * 0.4)),
          letterSpacing: initials.length > 1 ? '-0.02em' : undefined,
        }}
      >
        {initials}
      </span>
      {presence && (
        <span
          aria-hidden
          className="absolute right-0 bottom-0 rounded-full ring-2 ring-surface"
          style={{
            width: dot,
            height: dot,
            backgroundColor: PRESENCE_COLORS[presence],
            transform: 'translate(12%, 12%)',
          }}
        />
      )}
    </span>
  );
}

export interface AvatarPerson {
  id: string;
  name: string;
  colorHex?: string | null;
  presence?: Presence | null;
}

export interface AvatarStackProps {
  people: readonly AvatarPerson[];
  /** How many avatars before "+N" (iOS shows 4). */
  max?: number;
  size?: AvatarSize | number;
  /** Text when there is nobody (iOS "Unassigned"). Pass null to render nothing. */
  emptyLabel?: string | null;
  className?: string;
}

export function AvatarStack({ people, max = 4, size = 'sm', emptyLabel = 'Unassigned', className }: AvatarStackProps) {
  const px = avatarPixels(size);
  // De-duplicate by id (case-insensitive: payload ids are upper-case, server ids lower-case).
  const seen = new Set<string>();
  const list = people.filter((p) => {
    const key = p.id.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (list.length === 0) {
    if (emptyLabel === null) return null;
    return (
      <span className={cn('inline-flex items-center gap-1 text-xs text-fg-muted', className)}>
        <UserRoundX aria-hidden className="text-grey" style={{ width: px * 0.75, height: px * 0.75 }} />
        {emptyLabel}
      </span>
    );
  }
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  const overlap = -Math.round(px * 0.3);
  return (
    <span
      role="img"
      aria-label={list.map((p) => p.name).join(', ')}
      title={list.map((p) => p.name).join(', ')}
      className={cn('inline-flex items-center', className)}
    >
      {shown.map((p, i) => (
        <Avatar
          key={p.id}
          name={p.name}
          colorHex={p.colorHex}
          size={px}
          presence={p.presence}
          label={null}
          ringClassName="ring-[1.5px] ring-surface"
          className="drop-shadow-[0_1px_1px_rgb(16_42_94/0.12)]"
          style={{ marginLeft: i === 0 ? 0 : overlap, zIndex: shown.length - i }}
        />
      ))}
      {extra > 0 && (
        <span
          aria-hidden
          className="flex shrink-0 items-center justify-center rounded-full bg-grey font-rounded font-bold text-white ring-[1.5px] ring-surface"
          style={{ width: px, height: px, marginLeft: overlap, fontSize: Math.max(8, Math.round(px * 0.38)) }}
        >
          +{extra}
        </span>
      )}
    </span>
  );
}
